import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import routing from "routing";
import { IBiller, IBillerMessage } from "types/biller";
import { RemoveOverflow } from "utilities/helper";

export interface UseWelcomeMessageResult {
  shouldShowWelcomeBanner: boolean;
  welcomeMessage: string | undefined;
  isWelcomeModalOpen: boolean;
  openWelcomeModal: () => void;
  closeWelcomeModal: () => void;
}

export function useWelcomeMessage(
  biller: IBiller | undefined
): UseWelcomeMessageResult {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(true);

  const payableTypeCount = biller?.payableTypeDefinitions?.length ?? 0;
  const isPayableUiFlow =
    biller?.featureFlags?.payableUi?.toLowerCase() === "true";
  const isEligiblePage =
    (pathname === routing.index() &&
      (!isPayableUiFlow || payableTypeCount > 1)) ||
    (pathname === routing.lookup.index() && payableTypeCount === 1);

  const billerMessageProperties = biller?.properties?.find(
    (element) => element.name === "InstanceConfiguration"
  ) as IBillerMessage;

  const billerMessageEnabled =
    billerMessageProperties?.properties?.billerMessageWebEnabled === "true";

  let welcomeMessage: string | undefined;
  if (billerMessageEnabled) {
    try {
      const messages = JSON.parse(
        billerMessageProperties.properties.billerMessageWeb
      );
      welcomeMessage = messages.find(
        (message: { language: string }) => message.language === i18n.language
      )?.value;
    } catch (error) {
      welcomeMessage = undefined;
    }
  }

  const openWelcomeModal = () => {
    setIsWelcomeModalOpen(true);
  };

  const closeWelcomeModal = () => {
    setIsWelcomeModalOpen(false);
    window.setTimeout(RemoveOverflow, 50);
  };

  return {
    shouldShowWelcomeBanner:
      isEligiblePage && billerMessageEnabled && !!welcomeMessage,
    welcomeMessage,
    isWelcomeModalOpen,
    openWelcomeModal,
    closeWelcomeModal,
  };
}
